import {
  MaybeRefOrGetter,
  onBeforeUnmount,
  onMounted,
  readonly,
  ref,
  Ref,
  toValue,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { FeedAutoGenerationReason, FeedSummaryGenerationState } from '@/types';

type ShouldEnable = (
  summarizeDate: string | undefined,
) => FeedAutoGenerationReason | undefined;

// パーソナルニュース(ホーム画面)用
export const createPersonalNewsHomeShouldEnable = (): ShouldEnable => {
  return summarizeDate =>
    summarizeDate === undefined ? 'from_top' : undefined;
};

// パーソナルニュース(詳細画面)用
export const createPersonalNewsShouldEnable = (options: {
  dateStr: string;
  isFirst: boolean;
}): ShouldEnable => {
  return summarizeDate => {
    if (summarizeDate === undefined && options.isFirst) return 'from_top';
    if (summarizeDate === options.dateStr) return 'from_mail';
    return undefined;
  };
};

// テーマ(ニュース)用
export const createThemeNewsShouldEnable = (options: {
  dateStr: string;
  isFirst: boolean;
  isChinese: boolean;
}): ShouldEnable => {
  return summarizeDate => {
    if (options.isChinese) return undefined;
    if (summarizeDate === options.dateStr) return 'from_mail';
    if (summarizeDate === undefined && options.isFirst) return 'from_top';
    return undefined;
  };
};

// テーマ・パーソナルニュース(論文・特許)用
export const createTechnicalLiteratureShouldEnable = (options: {
  isPast: boolean;
  dateOptions: { value: string }[];
}): ShouldEnable => {
  return summarizeDate => {
    if (
      summarizeDate !== undefined &&
      options.isPast !==
        options.dateOptions.some(o => o.value === summarizeDate)
    )
      return 'from_mail';
    if (summarizeDate === undefined && !options.isPast) return 'from_top';
    return undefined;
  };
};

export const useAutoGenerateSummary = <F extends string>(
  generateButtonState: Ref<FeedSummaryGenerationState>,
  generateSummary: (data?: {
    feature?: F;
    autoGenReason?: FeedAutoGenerationReason;
  }) => void,
  feature: MaybeRefOrGetter<F>,
  shouldEnable: (
    summarizeDate: string | undefined,
  ) => FeedAutoGenerationReason | undefined,
) => {
  const route = useRoute();
  const autoGenReason = ref<FeedAutoGenerationReason | undefined>(undefined);
  let generated = false; // 二重実行防止

  onMounted(() => {
    const summarizeDate = route.query.summarize;
    autoGenReason.value = shouldEnable(
      typeof summarizeDate === 'string' ? summarizeDate : undefined,
    );
  });
  // watchはunmountで止まりはするが、unmountとほぼ同時にwatchの条件を満たすと
  // watchのコールバックが意図せず動くときがあるので、generated = trueにして
  // 意図しない自動生成が起こらないようにしておく
  onBeforeUnmount(() => {
    generated = true;
  });

  watch([autoGenReason, generateButtonState], ([auto, state]) => {
    // no-updateだと要約生成済みの状態があるということなのでgenerated扱いにする
    if (state === 'no-update') {
      generated = true;
    }
    if (auto !== undefined && state === 'ready' && !generated) {
      generateSummary({
        feature: toValue(feature),
        autoGenReason: autoGenReason.value,
      });
      generated = true;
    }
  });

  return { autoGenerationReason: readonly(autoGenReason) };
};
